import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { string, object } from 'prop-types';
import { Button, Skeleton, Spin, Empty } from 'antd';
import { Card } from '@cards/Card';
import { selectFormOrSharedPathwayForm } from '@pathways/redux/sharedPathways/reducers';
import { fetchForms } from '@redux/forms/actions';
import { submitFormAsAppUser } from '@redux/appUsers/actions';
import { selectAppUserFormSubmitting } from '@redux/appUsers/reducers';
import NamespacedTranslatedForm from '@components/TranslatedForm';
import ContentRef from '@utils/contentRef';
import { FORM, QUESTIONNAIRE, TASK } from '@utils/contentTypes';
import { fetchSharedPathwaySnapshots } from '@pathways/redux/sharedPathways/actions';
import './style.less';

function CallToActionForm({
  appUserId,
  contentRef,
  title,
  additionalFormDataToSubmit,
  formRef,
  formDataCached,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [formId, setFormId] = useState(null);
  const [formType, setFormType] = useState(FORM);
  const submitting = useSelector(selectAppUserFormSubmitting);

  useEffect(() => {
    dispatch(fetchSharedPathwaySnapshots());
  }, [dispatch]);

  // Parse contentRef to extract formId
  useEffect(() => {
    try {
      const parsedRef = ContentRef.fromString(contentRef);
      if (parsedRef.contentType === 'form') {
        setFormId(parsedRef.contentId);
      }
    } catch (error) {
      console.error('Error parsing contentRef:', error);
    }
  }, [contentRef]);

  // Fetch forms when formId is available
  useEffect(() => {
    if (formId) {
      dispatch(fetchForms());
    }
  }, [dispatch, formId]);

  const [loading, form] = useSelector(selectFormOrSharedPathwayForm(contentRef));

  // Determine form type from form metadata
  useEffect(() => {
    if (form?.metadata?.type) {
      const type = form.metadata.type;
      if (type === QUESTIONNAIRE || type === TASK) {
        setFormType(type);
      } else {
        setFormType(FORM);
      }
    }
  }, [form]);

  const handleSubmit = useCallback(
    async formSubmission => {
      if (!formId || !form) {
        return;
      }

      const formData = formSubmission.formData;
      const additionalFormData = additionalFormDataToSubmit ? additionalFormDataToSubmit : {};
      const combinedFormData = { ...formData, ...additionalFormData };
      const formAppToken = form.latestVersion.url.split('/')[6];
      try {
        dispatch(
          submitFormAsAppUser(contentRef, combinedFormData, appUserId, formType, formAppToken),
        );
        // Note: Success/error messages are handled by the saga
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [formId, form, additionalFormDataToSubmit, appUserId, formType, dispatch, contentRef],
  );

  if (!formId) {
    return (
      <Card className="call-to-action-form-card" title={title} loading={false}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Invalid form reference" />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="call-to-action-form-card" title={title} loading={false}>
        <Skeleton active />
      </Card>
    );
  }

  if (!form) {
    return (
      <Card className="call-to-action-form-card" title={title} loading={false}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('cards:CallToActionForm.formNotFound')}
        />
      </Card>
    );
  }

  return (
    <Card className="call-to-action-form-card" title={' '} loading={false}>
      <Spin spinning={submitting}>
        <NamespacedTranslatedForm
          schema={form?.latestVersion?.schema ?? {}}
          uiSchema={(form?.latestVersion?.uiSchema || form?.latestVersion?.ui_schema) ?? {}}
          namespace="forms"
          onSubmit={handleSubmit}
          formRef={formRef}
          formData={formDataCached ? formDataCached : undefined}
        >
          <div className="call-to-action-form-submit">
            <Button type="primary" htmlType="submit" disabled={submitting}>
              {t('cards:CallToActionForm.submitButton')}
            </Button>
          </div>
        </NamespacedTranslatedForm>
      </Spin>
    </Card>
  );
}

CallToActionForm.propTypes = {
  appUserId: string.isRequired,
  contentRef: string.isRequired,
  formRef: object.isRequired,
  formDataCached: object,
};

export default CallToActionForm;
